import React from 'react';
import { SecureMarkdownRenderer } from './SecureMarkdownRenderer';
import JsonViewer from "./JsonViewer";


function isPureJson(content: any): boolean {
    // Already an object
    if (typeof content === 'object' && content !== null) {
        return true;
    }

    // Not a string, can't be JSON
    if (typeof content !== 'string') {
        return false;
    }

    // Trim the string
    let trimmed = content.trim();

    // Remove surrounding quotes if present
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
        (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        trimmed = trimmed.slice(1, -1);
    }

    // Try to parse as JSON
    try {
        const parsed = JSON.parse(trimmed);
        // Must be object or array to be considered JSON
        return typeof parsed === 'object' && parsed !== null;
    } catch {
        return false;
    }
}


function parseJson(content: any): any {
    // Already an object
    if (typeof content === 'object' && content !== null) {
        return content;
    }

    // Must be a string to parse
    if (typeof content !== 'string') {
        throw new Error('Content is not JSON');
    }

    let trimmed = content.trim();

    // Remove surrounding quotes if present
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
        (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        trimmed = trimmed.slice(1, -1);
    }

    // Parse and return
    return JSON.parse(trimmed);
}


const ResponseRenderer = ({ response }: { response: any }) => {
    // Normalize content
    const rawContent = response?.content ?? response ?? '';

    // Handle empty content
    if (!rawContent || (typeof rawContent === 'string' && rawContent.trim() === '')) {
        return <div className="text-slate-400 text-sm italic">No content</div>;
    }

    // Step 1: Check if it's pure JSON
    if (isPureJson(rawContent)) {
        try {
            const jsonData = parseJson(rawContent);
            return <JsonViewer data={jsonData} maxHeight={"1200px"}/>;
        } catch (error) {
            console.warn('Failed to parse JSON:', error);
            // Fall through to mixed content renderer
        }
    }

    // Step 2: Treat as mixed content (text/markdown/code blocks)
    const content = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent, null, 2);
    // SecureMarkdownRenderer handles:
    // - Regular markdown
    // - JSON code blocks (```json)
    // - Mermaid diagrams (```mermaid)
    // - SVG (```svg)
    // - ASCII art
    return  <SecureMarkdownRenderer content={content} className="text-slate-200" />;
}

export default ResponseRenderer;